import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";
import { fetchGradePoints, resolveGradePointByPercentage } from "../../utils/amciDesign";

const API = window.location.pathname.includes("/finaloutput/public_html")
  ? "/finaloutput/public_html/api"
  : "/api";
const uploadsBase = "/uploads";

const cardGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "16px",
};

const statCard = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.98) 100%)",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  borderRadius: "22px",
  padding: "18px",
  boxShadow: "0 16px 28px rgba(15, 23, 42, 0.06)",
};

const tableWrap = { overflowX: "auto" };
const tableStyle = { width: "100%", borderCollapse: "collapse" };
const thStyle = { textAlign: "left", padding: "14px 12px", color: "#7c2d3a", borderBottom: "1px solid rgba(148, 163, 184, 0.16)", fontSize: "13px", background: "rgba(255,247,237,0.72)" };
const tdStyle = { padding: "14px 12px", borderBottom: "1px solid rgba(148, 163, 184, 0.12)", color: "#0f172a", verticalAlign: "top" };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", zIndex: 3000 };
const modalCardStyle = { background: "#fff", borderRadius: "22px", width: "min(760px, 100%)", maxHeight: "82vh", overflow: "auto", padding: "20px", boxShadow: "0 28px 80px rgba(15, 23, 42, 0.25)" };
const detailGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" };

function toDateInputValue(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (!match) return "";

  return `${match[3]}-${match[2]}-${match[1]}`;
}

function calculateCompletionDate(admissionDate, courseId, courses) {
  if (!admissionDate || !courseId) return "";
  const course = courses.find((item) => String(item.id) === String(courseId));
  const duration = String(course?.duration || "").toLowerCase();
  const matches = [...duration.matchAll(/(\d+(?:\.\d+)?)\s*(year|years|yr|yrs|month|months|mon|mons|week|weeks|day|days)/g)];
  if (!matches.length) return "";

  const date = new Date(`${admissionDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";

  matches.forEach((match) => {
    const amount = Number(match[1]);
    const unit = match[2];
    if (unit.startsWith("year") || unit.startsWith("yr")) {
      date.setMonth(date.getMonth() + Math.round(amount * 12));
    } else if (unit.startsWith("month") || unit.startsWith("mon")) {
      date.setMonth(date.getMonth() + Math.round(amount));
    } else if (unit.startsWith("week")) {
      date.setDate(date.getDate() + Math.round(amount * 7));
    } else if (unit.startsWith("day")) {
      date.setDate(date.getDate() + Math.round(amount));
    }
  });

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function fileUrl(fileName) {
  return fileName ? `${uploadsBase}/${fileName}` : "";
}

function ModuleHeader({ title, description }) {
  return (
    <div
      style={{
        marginBottom: "20px",
        background: "linear-gradient(135deg, rgba(219,234,254,0.62) 0%, rgba(255,247,237,0.92) 100%)",
        border: "1px solid rgba(148, 163, 184, 0.14)",
        borderRadius: "22px",
        padding: "18px 20px",
      }}
    >
      <h2 style={{ margin: "0 0 6px", color: "#4e1b24" }}>{title}</h2>
      <p style={{ color: "#6b3140", lineHeight: 1.7, marginBottom: 0 }}>{description}</p>
    </div>
  );
}

function StatusChip({ status }) {
  const current = status || "pending";
  const styles = {
    approved: { color: "#166534", background: "#dcfce7" },
    hold: { color: "#92400e", background: "#fef3c7" },
    rejected: { color: "#b91c1c", background: "#fee2e2" },
    pending: { color: "#1d4ed8", background: "#dbeafe" },
    not_started: { color: "#475569", background: "#e2e8f0" },
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "capitalize",
        ...(styles[current] || styles.pending),
      }}
    >
      {current}
    </span>
  );
}

function DetailTile({ label, value }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.86)", border: "1px solid rgba(148, 163, 184, 0.14)", borderRadius: "18px", padding: "14px", boxShadow: "0 12px 22px rgba(15, 23, 42, 0.04)" }}>
      <div style={{ color: "#7c2d3a", fontSize: "12px", marginBottom: "6px", fontWeight: 700 }}>{label}</div>
      <div style={{ color: "#0f172a", fontWeight: 600, wordBreak: "break-word" }}>{value || "-"}</div>
    </div>
  );
}

function StudentPreviewModal({ item, onClose }) {
  if (!item) return null;

  const documentItems = [
    { label: "Photo", key: "photo", isImage: true },
    { label: "Signature", key: "signature", isImage: true },
    { label: "ID Proof", key: "id_proof", isImage: false },
    { label: "Academic Document", key: "academic_document", isImage: false },
  ];

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "18px", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: "0 0 6px", color: "#0f172a" }}>Student Details</h3>
            <p style={{ margin: 0, color: "#64748b" }}>Student image, personal data, and uploaded documents.</p>
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button>
        </div>

        <div style={detailGridStyle}>
          <DetailTile label="Application No" value={item.application_no} />
          <DetailTile label="Registration No" value={item.registration_no} />
          <DetailTile label="Student Name" value={item.student_name} />
          <DetailTile label="Study Centre" value={item.study_centre_name || item.study_centre_login} />
          <DetailTile label="State" value={item.state_name} />
          <DetailTile label="District" value={item.district_name} />
          <DetailTile label="Course" value={item.course_name} />
          <DetailTile label="Phone" value={item.phone} />
          <DetailTile label="Email" value={item.email} />
          <DetailTile label="DOB" value={item.dob} />
          <DetailTile label="Father Name" value={item.father_name} />
          <DetailTile label="Admission Date" value={item.admission_date} />
          <DetailTile label="Course Completion" value={item.course_completion_date} />
          <DetailTile label="Guardian Name" value={item.guardian_name} />
          <DetailTile label="Status" value={item.status} />
          <DetailTile label="Stage Remark" value={item.workflow_stage_remark || item.status_remark} />
        </div>

        <div style={{ ...statCard, marginTop: "16px" }}>
          <div style={{ color: "#64748b", fontSize: "12px", marginBottom: "6px", fontWeight: 700 }}>Address</div>
          <div style={{ color: "#0f172a", whiteSpace: "pre-wrap" }}>{item.address || "-"}</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginTop: "16px" }}>
          {documentItems.map((documentItem) => (
            <div key={documentItem.key} style={{ ...statCard, textAlign: "center" }}>
              <div style={{ marginBottom: "10px", color: "#475569", fontWeight: 700 }}>{documentItem.label}</div>
              {item[documentItem.key] ? (
                documentItem.isImage ? (
                  <img src={fileUrl(item[documentItem.key])} alt={documentItem.label} style={{ width: "100%", maxHeight: "220px", objectFit: "contain", borderRadius: "12px", border: "1px solid #e2e8f0" }} />
                ) : (
                  <a href={fileUrl(item[documentItem.key])} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
                    Open {documentItem.label}
                  </a>
                )
              ) : (
                <div style={{ color: "#94a3b8" }}>Not uploaded</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StudentEditModal({ item, courses = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    student_name: item?.student_name || "",
    phone: item?.phone || "",
    email: item?.email || "",
    dob: toDateInputValue(item?.dob),
    session_year: item?.session_year || "",
    father_name: item?.father_name || "",
    admission_date: toDateInputValue(item?.admission_date),
    course_completion_date: toDateInputValue(item?.course_completion_date),
    guardian_name: item?.guardian_name || "",
    address: item?.address || "",
    course_id: item?.course_id || "",
  });
  const [files, setFiles] = useState({});
  const [saving, setSaving] = useState(false);
  const courseOptions = useMemo(() => {
    const list = Array.isArray(courses) ? [...courses] : [];
    if (item?.course_id && item?.course_name && !list.some((course) => String(course.id) === String(item.course_id))) {
      list.unshift({ id: item.course_id, title: item.course_name });
    }
    return list;
  }, [courses, item?.course_id, item?.course_name]);

  if (!item) return null;

  const updateField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "course_id" || key === "admission_date") {
        next.course_completion_date = calculateCompletionDate(next.admission_date, next.course_id, courseOptions) || next.course_completion_date || "";
      }
      return next;
    });
  };

  const saveEdit = async () => {
    try {
      setSaving(true);
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => payload.append(key, value || ""));
      Object.entries(files).forEach(([key, value]) => {
        if (value) payload.append(key, value);
      });
      await axios.post(`${API}/student-applications/${item.id}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSaved();
    } catch (error) {
      window.alert(error.response?.data?.message || "Student update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "18px", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: "0 0 6px", color: "#0f172a" }}>Edit Student</h3>
            <p style={{ margin: 0, color: "#64748b" }}>Applied student data can be corrected from here.</p>
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button>
        </div>

        <div className="row g-3">
          <div className="col-md-6"><label className="form-label">Student Name</label><input className="form-control" value={form.student_name} onChange={(e) => setForm((prev) => ({ ...prev, student_name: e.target.value }))} /></div>
          <div className="col-md-6"><label className="form-label">Phone</label><input className="form-control" value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} /></div>
          <div className="col-md-6"><label className="form-label">Email</label><input className="form-control" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} /></div>
          <div className="col-md-6"><label className="form-label">DOB</label><input className="form-control" type="date" value={form.dob} onChange={(e) => setForm((prev) => ({ ...prev, dob: e.target.value }))} /></div>
          <div className="col-md-6"><label className="form-label">Session</label><input className="form-control" value={form.session_year} onChange={(e) => updateField("session_year", e.target.value)} /></div>
          <div className="col-md-6"><label className="form-label">Father Name</label><input className="form-control" value={form.father_name} onChange={(e) => setForm((prev) => ({ ...prev, father_name: e.target.value }))} /></div>
          <div className="col-md-6">
            <label className="form-label">Course</label>
            <select className="form-select" value={form.course_id} onChange={(e) => updateField("course_id", e.target.value)}>
              <option value="">Select Course</option>
              {courseOptions.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6"><label className="form-label">Admission Date</label><input className="form-control" type="date" value={form.admission_date} onChange={(e) => updateField("admission_date", e.target.value)} /></div>
          <div className="col-md-6"><label className="form-label">Course Completion</label><input className="form-control" type="date" value={form.course_completion_date} readOnly /></div>
          <div className="col-md-6"><label className="form-label">Guardian Name</label><input className="form-control" value={form.guardian_name} onChange={(e) => setForm((prev) => ({ ...prev, guardian_name: e.target.value }))} /></div>
          <div className="col-12"><label className="form-label">Address</label><textarea className="form-control" rows="3" value={form.address} onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}></textarea></div>
          {["photo", "signature", "id_proof", "academic_document"].map((field) => (
            <div className="col-md-6" key={field}>
              <label className="form-label text-capitalize">{field.replaceAll("_", " ")}</label>
              <input className="form-control" type="file" onChange={(e) => setFiles((prev) => ({ ...prev, [field]: e.target.files?.[0] || null }))} />
            </div>
          ))}
        </div>

        <div style={{ marginTop: "16px" }}>
          <button type="button" className="btn btn-primary" onClick={saveEdit} disabled={saving}>{saving ? "Saving..." : "Save Edit"}</button>
        </div>
      </div>
    </div>
  );
}

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const passMarkFromFull = (value) => {
  const fullMarks = numberValue(value);
  return fullMarks > 0 ? Number((fullMarks * 0.3).toFixed(2)) : 0;
};

const normalizeMarksRow = (row) => ({
  ...row,
  pass_marks: row.pass_marks === "" || row.pass_marks === null || Number(row.pass_marks || 0) === 0 ? passMarkFromFull(row.max_marks) : row.pass_marks,
  practical_pass_marks: row.practical_pass_marks === "" || row.practical_pass_marks === null || Number(row.practical_pass_marks || 0) === 0 ? passMarkFromFull(row.practical_max_marks) : row.practical_pass_marks,
  project_pass_marks: row.project_pass_marks === "" || row.project_pass_marks === null || Number(row.project_pass_marks || 0) === 0 ? passMarkFromFull(row.project_max_marks) : row.project_pass_marks,
  viva_pass_marks: row.viva_pass_marks === "" || row.viva_pass_marks === null || Number(row.viva_pass_marks || 0) === 0 ? passMarkFromFull(row.viva_max_marks) : row.viva_pass_marks,
});

const marksHintStyle = {
  marginTop: "6px",
  color: "#64748b",
  fontSize: "11px",
  fontWeight: 700,
  lineHeight: 1.35,
};

function MarksEntryModal({ item, onClose, onSubmitted }) {
  const [marksData, setMarksData] = useState(null);
  const [gradeRows, setGradeRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const [marksRes, gradePointRows] = await Promise.all([
          axios.get(`${API}/student-applications/${item.id}/marks`),
          fetchGradePoints(),
        ]);
        if (active) {
          setMarksData({
            ...marksRes.data,
            rows: (marksRes.data?.rows || []).map(normalizeMarksRow),
          });
          setGradeRows(gradePointRows);
        }
      } catch (error) {
        if (active) window.alert(error.response?.data?.message || "Marks data load failed");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [item.id]);

  const updateMarksRow = (moduleId, key, value) => {
    const passFieldByFullField = {
      max_marks: "pass_marks",
      practical_max_marks: "practical_pass_marks",
      project_max_marks: "project_pass_marks",
      viva_max_marks: "viva_pass_marks",
    };
    const fullFieldByObtainedField = {
      marks_obtained: "max_marks",
      practical_marks_obtained: "practical_max_marks",
      project_marks_obtained: "project_max_marks",
      viva_marks_obtained: "viva_max_marks",
    };

    setMarksData((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => {
        if (row.module_id !== moduleId) return row;
        let nextValue = value;
        const fullField = fullFieldByObtainedField[key];
        if (fullField && value !== "") {
          const fullMarks = numberValue(row[fullField]);
          const obtainedMarks = Math.max(0, numberValue(value));
          nextValue = fullMarks > 0 && obtainedMarks > fullMarks ? String(fullMarks) : String(obtainedMarks);
        }
        const nextRow = { ...row, [key]: nextValue };
        const passField = passFieldByFullField[key];
        if (passField) {
          nextRow[passField] = passMarkFromFull(value);
        }
        return nextRow;
      }),
    }));
  };

  const totals = useMemo(() => {
    const rows = marksData?.rows || [];
    const theoryObtained = rows.reduce((sum, row) => sum + numberValue(row.marks_obtained), 0);
    const practicalObtained = rows.reduce((sum, row) => sum + numberValue(row.practical_marks_obtained), 0);
    const projectObtained = rows.reduce((sum, row) => sum + numberValue(row.project_marks_obtained), 0);
    const vivaObtained = rows.reduce((sum, row) => sum + numberValue(row.viva_marks_obtained), 0);
    const theoryFull = rows.reduce((sum, row) => sum + numberValue(row.max_marks), 0);
    const practicalFull = rows.reduce((sum, row) => sum + numberValue(row.practical_max_marks), 0);
    const projectFull = rows.reduce((sum, row) => sum + numberValue(row.project_max_marks), 0);
    const vivaFull = rows.reduce((sum, row) => sum + numberValue(row.viva_max_marks), 0);
    const theoryPass = rows.reduce((sum, row) => sum + numberValue(row.pass_marks), 0);
    const practicalPass = rows.reduce((sum, row) => sum + numberValue(row.practical_pass_marks), 0);
    const projectPass = rows.reduce((sum, row) => sum + numberValue(row.project_pass_marks), 0);
    const vivaPass = rows.reduce((sum, row) => sum + numberValue(row.viva_pass_marks), 0);
    const totalObtained = theoryObtained + practicalObtained + projectObtained + vivaObtained;
    const totalFull = theoryFull + practicalFull + projectFull + vivaFull;
    const totalPass = theoryPass + practicalPass + projectPass + vivaPass;
    const percentageValue = totalFull > 0 ? (totalObtained / totalFull) * 100 : 0;
    const gradeInfo = resolveGradePointByPercentage(percentageValue, gradeRows);

    return {
      theoryObtained,
      practicalObtained,
      projectObtained,
      vivaObtained,
      theoryFull,
      practicalFull,
      projectFull,
      vivaFull,
      theoryPass,
      practicalPass,
      projectPass,
      vivaPass,
      totalObtained,
      totalFull,
      totalPass,
      percentage: percentageValue.toFixed(2),
      grade: gradeInfo.grade,
      gradePoint: gradeInfo.grade_point,
    };
  }, [gradeRows, marksData?.rows]);

  const submitMarks = async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/student-applications/${item.id}/marks`, {
        rows: (marksData.rows || []).map(normalizeMarksRow),
      });
      onSubmitted();
    } catch (error) {
      window.alert(error.response?.data?.message || "Marks save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "18px", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: "0 0 6px", color: "#0f172a" }}>Marks Entry</h3>
            <p style={{ margin: 0, color: "#64748b" }}>{item.student_name} - {item.course_name || "No course"}</p>
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button>
        </div>

        {loading ? (
          <p>Loading marks data...</p>
        ) : !marksData?.rows?.length ? (
          <div style={statCard}>No syllabus or course module found for this course.</div>
        ) : (
          <>
            <div style={tableWrap}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Semester</th>
                    <th style={thStyle}>Module</th>
                    <th style={thStyle}>Theory</th>
                    <th style={thStyle}>Practical</th>
                    <th style={thStyle}>Project / Assignment</th>
                    <th style={thStyle}>Viva</th>
                    <th style={thStyle}>Approval</th>
                  </tr>
                </thead>
                <tbody>
                  {marksData.rows.map((row) => (
                    <tr key={row.module_id}>
                      <td style={tdStyle}>{row.semester_title || `Semester ${row.semester_no || 1}`}</td>
                      <td style={tdStyle}>{row.module_title}</td>
                      <td style={tdStyle}>
                        <input className="form-control form-control-sm" type="number" min="0" max={row.max_marks} placeholder="Obtained" value={row.marks_obtained} onChange={(e) => updateMarksRow(row.module_id, "marks_obtained", e.target.value)} />
                        <div style={marksHintStyle}>Full: {row.max_marks || 0} | Pass: {row.pass_marks || 0}</div>
                      </td>
                      <td style={tdStyle}>
                        <input className="form-control form-control-sm" type="number" min="0" max={row.practical_max_marks} placeholder="Obtained" value={row.practical_marks_obtained} onChange={(e) => updateMarksRow(row.module_id, "practical_marks_obtained", e.target.value)} />
                        <div style={marksHintStyle}>Full: {row.practical_max_marks || 0} | Pass: {row.practical_pass_marks || 0}</div>
                      </td>
                      <td style={tdStyle}>
                        <input className="form-control form-control-sm" type="number" min="0" max={row.project_max_marks} placeholder="Obtained" value={row.project_marks_obtained} onChange={(e) => updateMarksRow(row.module_id, "project_marks_obtained", e.target.value)} />
                        <div style={marksHintStyle}>Full: {row.project_max_marks || 0} | Pass: {row.project_pass_marks || 0}</div>
                      </td>
                      <td style={tdStyle}>
                        <input className="form-control form-control-sm" type="number" min="0" max={row.viva_max_marks} placeholder="Obtained" value={row.viva_marks_obtained} onChange={(e) => updateMarksRow(row.module_id, "viva_marks_obtained", e.target.value)} />
                        <div style={marksHintStyle}>Full: {row.viva_max_marks || 0} | Pass: {row.viva_pass_marks || 0}</div>
                      </td>
                      <td style={tdStyle}><StatusChip status={row.approval_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
              <DetailTile label="Theory Obtained" value={totals.theoryObtained} />
              <DetailTile label="Practical Obtained" value={totals.practicalObtained} />
              <DetailTile label="Project / Assignment" value={totals.projectObtained} />
              <DetailTile label="Viva Obtained" value={totals.vivaObtained} />
              <DetailTile label="Total Obtained" value={totals.totalObtained} />
              <DetailTile label="Theory Full Marks" value={totals.theoryFull} />
              <DetailTile label="Practical Full Marks" value={totals.practicalFull} />
              <DetailTile label="Project / Assignment Full" value={totals.projectFull} />
              <DetailTile label="Viva Full Marks" value={totals.vivaFull} />
              <DetailTile label="Total Full Marks" value={totals.totalFull} />
              <DetailTile label="Theory Pass Marks" value={totals.theoryPass} />
              <DetailTile label="Practical Pass Marks" value={totals.practicalPass} />
              <DetailTile label="Project / Assignment Pass" value={totals.projectPass} />
              <DetailTile label="Viva Pass Marks" value={totals.vivaPass} />
              <DetailTile label="Total Pass Marks" value={totals.totalPass} />
              <DetailTile label="Percentage" value={`${totals.percentage}%`} />
              <DetailTile label="Grade" value={totals.grade} />
              <DetailTile label="Grade Point" value={totals.gradePoint || "-"} />
            </div>
            <div style={{ marginTop: "16px" }}>
              <button type="button" className="btn btn-primary" onClick={submitMarks} disabled={saving}>
                {saving ? "Submitting..." : "Send To Admin Approval"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StudentStagePage({ title, description, workflowStage, actionButtons, allowEdit = false, allowMarks = false }) {
  const { franchise, selectedSession } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [marksItem, setMarksItem] = useState(null);
  const [actionState, setActionState] = useState({ openId: null, remark: "", loading: false });
  const [modalLoading, setModalLoading] = useState(false);

  const loadRows = useCallback(async () => {
    if (!franchise?.id) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/franchise-portal/${franchise.id}/applications`, {
        params: { workflow_stage: workflowStage, session_year: selectedSession, _ts: Date.now() },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [franchise?.id, selectedSession, workflowStage]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    let active = true;
    if (!franchise?.id || !allowEdit) {
      setCourses([]);
      return () => {
        active = false;
      };
    }

    axios
      .get(`${API}/franchise-portal/${franchise.id}/courses`, { params: { _ts: Date.now() } })
      .then((res) => {
        if (active) setCourses(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (active) setCourses([]);
      });

    return () => {
      active = false;
    };
  }, [allowEdit, franchise?.id]);

  const runAction = async (row, config) => {
    if (config.requireRemark && !actionState.remark.trim()) {
      window.alert("Remark is required for this action");
      return;
    }

    try {
      setActionState((prev) => ({ ...prev, loading: true }));
      await axios.post(`${API}/franchise-portal/applications/${row.id}/stage-action`, {
        action: config.action,
        remark: actionState.remark.trim(),
        actor_id: franchise?.login_id || "franchise_admin",
      });
      setActionState({ openId: null, remark: "", loading: false });
      await loadRows();
    } catch (error) {
      window.alert(error.response?.data?.message || "Stage update failed");
      setActionState((prev) => ({ ...prev, loading: false }));
    }
  };

  const openViewWithFullData = async (rowId) => {
    try {
      setModalLoading(true);
      const res = await axios.get(`${API}/student-applications/${rowId}`);
      setSelected(res.data || null);
    } catch (error) {
      window.alert(error.response?.data?.message || "Student details load failed");
    } finally {
      setModalLoading(false);
    }
  };

  const openEditWithFullData = async (rowId) => {
    try {
      setModalLoading(true);
      const res = await axios.get(`${API}/student-applications/${rowId}`);
      setEditingItem(res.data || null);
    } catch (error) {
      window.alert(error.response?.data?.message || "Edit data load failed");
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div>
      <ModuleHeader title={title} description={description} />
      <div style={cardGrid}>
        <div style={statCard}>
          <p style={{ margin: 0, color: "#64748b", fontWeight: 700 }}>Total Students</p>
          <h3 style={{ margin: "10px 0 0" }}>{rows.length}</h3>
        </div>
        <div style={statCard}>
          <p style={{ margin: 0, color: "#64748b", fontWeight: 700 }}>Workflow Stage</p>
          <h3 style={{ margin: "10px 0 0", textTransform: "capitalize" }}>{workflowStage.replaceAll("_", " ")}</h3>
        </div>
      </div>

      <div style={{ ...statCard, marginTop: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0, color: "#0f172a" }}>{title} List</h3>
            <p style={{ margin: "6px 0 0", color: "#64748b" }}>Selected session {selectedSession || "-"} অনুযায়ী student rows show হচ্ছে.</p>
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={loadRows} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Sl No</th>
                <th style={thStyle}>Student</th>
                <th style={thStyle}>Course</th>
                <th style={thStyle}>Session</th>
                <th style={thStyle}>{allowMarks ? "Marks Approval" : "Status"}</th>
                <th style={thStyle}>Workflow Remark</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.map((row, index) => (
                <tr key={row.id}>
                  <td style={tdStyle}>{index + 1}</td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <img
                        src={row.photo ? fileUrl(row.photo) : "https://placehold.co/52x52/e2e8f0/475569?text=ST"}
                        alt={row.student_name}
                        style={{ width: "52px", height: "52px", borderRadius: "16px", objectFit: "cover", border: "1px solid #e2e8f0" }}
                      />
                      <div>
                        <div style={{ fontWeight: 700 }}>{row.student_name}</div>
                        <div style={{ color: "#64748b", fontSize: "12px" }}>{row.application_no || "-"}</div>
                        <div style={{ color: "#7c2d3a", fontSize: "12px", fontWeight: 700 }}>{row.registration_no || "Not registered yet"}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}>{row.course_name || "-"}</td>
                  <td style={tdStyle}>{row.session_year || "-"}</td>
                  <td style={tdStyle}>
                    <StatusChip status={allowMarks ? row.marks_approval_status : row.status} />
                  </td>
                  <td style={tdStyle}>
                    {row.workflow_stage_remark || row.status_remark || (
                      allowMarks
                        ? row.marks_approval_status === "approved"
                          ? "Head of Institute approved the submitted marks."
                          : row.marks_approval_status === "rejected"
                          ? "Marks rejected by Head of Institute. Please check admin remark."
                          : row.marks_approval_status === "pending"
                          ? "Marks submitted and waiting for Head of Institute approval."
                          : "Marks not entered yet."
                        : "-"
                    )}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openViewWithFullData(row.id)} disabled={modalLoading}>{modalLoading ? "Loading..." : "View"}</button>
                      {allowEdit ? <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openEditWithFullData(row.id)} disabled={modalLoading}>{modalLoading ? "Loading..." : "Edit"}</button> : null}
                      {allowMarks ? (
                        <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setMarksItem(row)}>Marks Entry</button>
                      ) : (
                        <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => setActionState({ openId: row.id, remark: "", loading: false })}>Action</button>
                      )}
                    </div>
                    {!allowMarks && actionState.openId === row.id ? (
                      <div style={{ marginTop: "10px", display: "grid", gap: "8px", minWidth: "220px" }}>
                        <textarea className="form-control" rows="3" placeholder="Remark when needed" value={actionState.remark} onChange={(e) => setActionState((prev) => ({ ...prev, remark: e.target.value }))}></textarea>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                          {actionButtons.map((button) => (
                            <button key={button.label} type="button" className={`btn btn-sm ${button.className}`} disabled={actionState.loading} onClick={() => runAction(row, button)}>
                              {button.label}
                            </button>
                          ))}
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setActionState({ openId: null, remark: "", loading: false })}>Close</button>
                        </div>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 ? (
                <tr><td style={tdStyle} colSpan={7}>No student found for session {selectedSession || "-"} in this stage.</td></tr>
              ) : null}
              {loading ? (
                <tr><td style={tdStyle} colSpan={7}>Loading students...</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <StudentPreviewModal item={selected} onClose={() => setSelected(null)} />
      {editingItem ? <StudentEditModal item={editingItem} courses={courses} onClose={() => setEditingItem(null)} onSaved={() => { setEditingItem(null); loadRows(); }} /> : null}
      {marksItem ? <MarksEntryModal item={marksItem} onClose={() => setMarksItem(null)} onSubmitted={() => { setMarksItem(null); loadRows(); }} /> : null}
    </div>
  );
}

export function AppliedStudents() {
  return (
    <StudentStagePage
      title="Applied Student"
      description="New student entries land here first. Franchise can edit full data and move them to accepted or keep them on hold with remarks."
      workflowStage="applied"
      allowEdit
      actionButtons={[
        { label: "Accept", action: "accept_application", className: "btn-success" },
        { label: "Hold", action: "hold_application", className: "btn-warning", requireRemark: true },
      ]}
    />
  );
}

export function AcceptedStudents() {
  return (
    <StudentStagePage
      title="Accepted Student"
      description="Accepted students wait here until the franchise sends them to master admin for registration approval."
      workflowStage="accepted"
      actionButtons={[
        { label: "Registered Student", action: "register_student", className: "btn-success" },
        { label: "Reject Student", action: "reject_student", className: "btn-danger", requireRemark: true },
      ]}
    />
  );
}

export function RegisteredStudents() {
  return (
    <StudentStagePage
      title="Registered Student"
      description="Students approved by master admin move here with registration number. Once exam is completed, shift them to the exam completed stage."
      workflowStage="registered"
      actionButtons={[
        { label: "Exam Complete", action: "complete_exam", className: "btn-primary" },
      ]}
    />
  );
}

export function ExamCompletedStudents() {
  return (
    <StudentStagePage
      title="Exam Completed Student"
      description="Marks entry can be sent from here for Head of Institute approval. After approval students move to marksheet and certificate download menus."
      workflowStage="exam_completed"
      allowMarks
      actionButtons={[]}
    />
  );
}
